<template>
  <div class="column content-box">
    <search-filters
      :fields="searchFields"
      :filters="filters"
      :disabled="teamUIFlags.isFetching"
      :placeholder="$t('TEAMS_SETTINGS.SEARCH.PLACEHOLDER')"
      @update:filters="updateFilters"
      @search-field-change="onSearchFieldChange"
      @apply="applySearch"
    />
    <div class="row">
      <div class="small-8 columns with-right-space ">
        <p
          v-if="!teamUIFlags.isFetching && !filteredTeams.length"
          class="no-items-error-message"
        >
          {{ $t('TEAMS_SETTINGS.LIST.404') }}
          <router-link
            v-if="isAdmin && !teamsList.length"
            :to="addAccountScoping('settings/teams/new')"
          >
            {{ $t('TEAMS_SETTINGS.NEW_TEAM') }}
          </router-link>
        </p>

        <table v-if="filteredTeams.length" class="woot-table">
          <tbody>
            <tr v-for="item in filteredTeams" :key="item.id">
              <td>
                <span class="agent-name">{{ item.name }}</span>
                <p>{{ item.description }}</p>
              </td>
              <td>
                <span class="agent-name">{{ $t('TEAMS_SETTINGS.SEARCH.FIELDS.SUPERVISOR') }}</span>
                <p>{{ item.supervisor_id ? item.supervisor_name : '-' }}</p>
              </td>
              <td>
                <span class="agent-name">{{ $t('TEAMS_SETTINGS.SEARCH.OPTIONS.PRIVATE') }}</span>
                <spinner v-if="isPrivacyUpdating && loading[item.id]"
                  :colorScheme="'dark'"
                />
                <woot-switch v-else
                  :value="item.private"
                  class="toggle-private--switch"
                  @input="changePrivacy(item, item.private)"
                  :disabled="isPrivacyUpdating"
                />
              </td>
              <td>
                <div class="button-wrapper">
                  <router-link
                    :to="addAccountScoping(`settings/teams/${item.id}/edit`)"
                  >
                    <woot-button
                      v-if="isAdmin"
                      v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                      variant="smooth"
                      size="tiny"
                      color-scheme="secondary"
                      class-names="grey-btn"
                      icon="settings"
                    />
                  </router-link>
                  <woot-button
                    v-if="isAdmin"
                    v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    :is-loading="loading[item.id]"
                    @click="openDelete(item)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="small-4 columns">
        <span
          v-dompurify-html="
            $t('TEAMS_SETTINGS.SIDEBAR_TXT', {
              installationName: globalConfig.installationName,
            })
          "
        />
      </div>
    </div>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      :show.sync="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import adminMixin from '../../../../mixins/isAdmin';
import accountMixin from '../../../../mixins/account';
import alertMixin from 'shared/mixins/alertMixin';
import SearchFilters from '../../../../components/ui/filters/SearchFilters.vue';
import Spinner from '../../../../../shared/components/Spinner.vue';

const DEFAULT_FILTERS = {
  searchField: 'name',
  searchQuery: '',
  appliedSearchQuery: '',
};

const NO_SUPERVISOR_OPTION = 'without_supervisor';

const normalizeToBooleanString = value => String(Boolean(value));

const buildBooleanFilterOptions = ({ trueLabel, falseLabel }) => {
  const options = [ {value: 'true', label: trueLabel }, { value: 'false', label: falseLabel }];

  return options;
};

export default {
  components: {
    SearchFilters,
    Spinner,
  },
  mixins: [adminMixin, accountMixin, alertMixin],
  data() {
    return {
      loading: {},
      showSettings: false,
      showDeletePopup: false,
      selectedTeam: {},
      filters: { ...DEFAULT_FILTERS },
      isPrivacyUpdating: false,
    };
  },
  computed: {
    ...mapGetters({
      teamsList: 'teams/getTeams',
      teamUIFlags: 'teams/getUIFlags',
      globalConfig: 'globalConfig/get',
    }),
    searchFields() {
      return [
        {
          key: 'name',
          type: 'text',
          label: this.$t('TEAMS_SETTINGS.SEARCH.FIELDS.NAME'),
        },
        {
          key: 'description',
          type: 'text',
          label: this.$t('TEAMS_SETTINGS.SEARCH.FIELDS.DESCRIPTION'),
        },
        {
          key: 'allow_auto_assign',
          type: 'select',
          label: this.$t('TEAMS_SETTINGS.SEARCH.FIELDS.AUTO_ASSIGN'),
          options: this.allowAutoAssignOptions,
        },
        {
          key: 'supervisor_id',
          type: 'select',
          label: this.$t('TEAMS_SETTINGS.SEARCH.FIELDS.SUPERVISOR'),
          options: this.supervisorOptions,
        },
        {
          key: 'private',
          type: 'select',
          label: this.$t('TEAMS_SETTINGS.SEARCH.FIELDS.PRIVACY'),
          options: this.privacyOptions,
        },
      ];
    },
    allowAutoAssignOptions() {
      return buildBooleanFilterOptions({
        trueLabel: this.$t('TEAMS_SETTINGS.SEARCH.OPTIONS.YES'),
        falseLabel: this.$t('TEAMS_SETTINGS.SEARCH.OPTIONS.NO'),
      });
    },
    privacyOptions() {
      return buildBooleanFilterOptions({
        trueLabel: this.$t('TEAMS_SETTINGS.SEARCH.OPTIONS.PRIVATE'),
        falseLabel: this.$t('TEAMS_SETTINGS.SEARCH.OPTIONS.PUBLIC'),
      });
    },
    supervisorOptions() {
      const supervisorsMap = {};

      this.teamsList.forEach(team => {
        if (team.supervisor_id === null || team.supervisor_id === '') {
          return;
        }

        const id = String(team.supervisor_id);
        if (!supervisorsMap[id]) {
          supervisorsMap[id] = team.supervisor_name || id;
        }
      });

      const options = Object.keys(supervisorsMap)
        .map(id => ({
          value: id,
          label: supervisorsMap[id],
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
        );

      options.unshift({
        value: NO_SUPERVISOR_OPTION,
        label: this.$t('TEAMS_SETTINGS.SEARCH.OPTIONS.NO_SUPERVISOR'),
      });

      return options;
    },
    filteredTeams() {
      const { searchField, appliedSearchQuery } = this.filters;

      if (!appliedSearchQuery) {
        return this.teamsList;
      }

      const query = appliedSearchQuery.toLowerCase().trim();

      return this.teamsList.filter(team => {
        if (searchField === 'name' || searchField === 'description') {
          return (team[searchField] || '').toLowerCase().includes(query);
        }

        if (
          searchField === 'allow_auto_assign' &&
          this.hasFieldOption(searchField, appliedSearchQuery)
        ) {
          return (
            normalizeToBooleanString(team.allow_auto_assign) ===
            appliedSearchQuery
          );
        }

        if (
          searchField === 'private' &&
          this.hasFieldOption(searchField, appliedSearchQuery)
        ) {
          return normalizeToBooleanString(team.private) === appliedSearchQuery;
        }

        if (
          searchField === 'supervisor_id' &&
          this.hasFieldOption(searchField, appliedSearchQuery)
        ) {
          if (appliedSearchQuery === NO_SUPERVISOR_OPTION) {
            return team.supervisor_id === null || team.supervisor_id === '';
          }

          return String(team.supervisor_id) === appliedSearchQuery;
        }

        return true;
      });
    },
    deleteConfirmText() {
      return `${this.$t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${
        this.selectedTeam.name
      }`;
    },
    deleteRejectText() {
      return this.$t('TEAMS_SETTINGS.DELETE.CONFIRM.NO');
    },
    confirmDeleteTitle() {
      return this.$t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
        teamName: this.selectedTeam.name,
      });
    },
    confirmPlaceHolderText() {
      return `${this.$t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
        teamName: this.selectedTeam.name,
      })}`;
    },
  },
  watch: {
    teamsList: {
      handler() {
        this.sanitizeCurrentFilters();
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('teams/get');
  },
  methods: {
    getFieldByKey(fieldKey) {
      return this.searchFields.find(field => field.key === fieldKey) || null;
    },
    getFieldOptions(fieldKey) {
      const field = this.getFieldByKey(fieldKey);
      return field && field.type === 'select' ? field.options || [] : [];
    },
    hasFieldOption(fieldKey, value) {
      return this.getFieldOptions(fieldKey).some(
        option => option.value === value
      );
    },
    getFirstFieldOption(fieldKey) {
      const [firstOption] = this.getFieldOptions(fieldKey);
      return (firstOption && firstOption.value) || '';
    },
    updateSelectFilterValue(fieldKey, preserveSearchField = false) {
      const nextValue = this.getFirstFieldOption(fieldKey);
      this.filters = {
        ...this.filters,
        ...(preserveSearchField ? {} : { searchField: fieldKey }),
        searchQuery: nextValue,
        appliedSearchQuery: nextValue,
      };
    },
    updateFilters(nextFilters) {
      this.filters = {
        ...this.filters,
        ...nextFilters,
      };
    },
    sanitizeCurrentFilters() {
      const { searchField } = this.filters;
      const field = this.getFieldByKey(searchField);

      if (!field || field.type !== 'select') {
        return;
      }

      if (this.hasFieldOption(searchField, this.filters.searchQuery)) {
        return;
      }

      this.updateSelectFilterValue(searchField, true);
    },
    onSearchFieldChange(fieldKey) {
      const field = this.getFieldByKey(fieldKey);
      if (!field) {
        return;
      }

      if (field.type === 'select') {
        this.updateSelectFilterValue(fieldKey);
        return;
      }

      this.filters = {
        ...this.filters,
        searchField: fieldKey,
        searchQuery: '',
        appliedSearchQuery: '',
      };
    },
    applySearch() {
      this.filters = {
        ...this.filters,
        appliedSearchQuery: this.filters.searchQuery,
      };
    },
    async deleteTeam({ id }) {
      try {
        await this.$store.dispatch('teams/delete', id);
        this.showAlert(this.$t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
      }
    },
    async changePrivacy(item, status) {
      this.isPrivacyUpdating = true;
      this.loading = { ...this.loading, [item.id]: true };
      const minDelay = new Promise(resolve => setTimeout(resolve, 1000));
      try {
        await Promise.all([
          this.$store.dispatch('teams/update', {
            id: item.id,
            private: !status,
          }),
          minDelay,
        ]);
        this.showAlert(
          this.$t('TEAMS_SETTINGS.UPDATE_PRIVACY.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        await minDelay;
        this.showAlert(
          this.$t('TEAMS_SETTINGS.UPDATE_PRIVACY.API.ERROR_MESSAGE')
        );
      } finally {
        this.isPrivacyUpdating = false;
        this.loading = { ...this.loading, [item.id]: false };
      }
    },

    confirmDeletion() {
      this.deleteTeam(this.selectedTeam);
      this.closeDelete();
    },
    openDelete(team) {
      this.showDeletePopup = true;
      this.selectedTeam = team;
    },
    closeDelete() {
      this.showDeletePopup = false;
      this.selectedTeam = {};
    },
  },
};
</script>
<style lang="scss" scoped>
.button-wrapper {
  min-width: unset;
  justify-content: flex-end;
  padding-right: var(--space-large);
}
.content-box {
  padding-bottom: var(--space-small);
  margin-bottom: var(--space-small);
}

.woot-table {
  width: 100%;
  margin-bottom: var(--space-small);
}

.row {
  margin-bottom: var(--space-large);
}

.toggle-private--switch {
  margin-bottom: var(--space-small);
}
</style>
